import axios from "axios";
import { clearAuthStorage } from "../utils/authStorage";

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 30000,
    headers: {
        "Content-Type": "application/json",
    },
});

// Attach token to every request
axiosInstance.interceptors.request.use(config => {
    const token = localStorage.getItem("Token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Track refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(token);
    });
    failedQueue = [];
};

const forceLogout = () => {
    clearAuthStorage();
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    window.dispatchEvent(new CustomEvent('auth:logout', {
        detail: { reason: 'Session expired' }
    }));
};

// Handle 401 errors — attempt token refresh before logging out
axiosInstance.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        // Training gate (user 2026-09-24): the API refuses everything but tutorials, the quiz
        // and the user's own profile until the RSC certification is passed. Correct the local
        // flag so the menu and the route guard agree with the server — but do NOT navigate.
        //
        // It used to jump to /quiz here. The sidebar asks for commission availability on EVERY
        // page, that call is refused for a trainee, and the jump reloaded the page, which asked
        // again — tutorials and the quiz sat there blinking. Navigation belongs to the route
        // guard, which already knows where a trainee may go. (user 2026-09-25)
        if (error.response?.status === 403 && error.response?.data?.trainingGate) {
            try { localStorage.setItem("CanCreateSale", "false"); } catch { /* private mode */ }
            return Promise.reject(error);
        }

        /* A failed sign-in is not an expired session. These three are called with no
           session at all, and their 401 IS the answer - UserLogin's body carries which
           of the three went wrong (no such username / wrong password / deactivated).
           They used to fall into the refresh path below, and for anyone who had been
           logged in before, the leftover UserId + RefreshToken in localStorage sent it
           off to RefreshToken, which failed, and the rejection carried THAT error
           instead. The reason never reached the page, so the sign-in screen could only
           ever say "Login failed. Please try again." - which is exactly what it said.
           (user 2026-10-18, still locked out on the live site) */
        const isAnonymousAuthCall = /\/(User\/UserLogin|Auth\/ForgotPassword|Auth\/ResetPassword)/i
            .test(originalRequest?.url || "");
        if (isAnonymousAuthCall) {
            return Promise.reject(error);
        }

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            console.log('[Auth] 401 on', originalRequest.url, '— attempting refresh. Token exists:', !!localStorage.getItem("Token"), 'RefreshToken exists:', !!localStorage.getItem("RefreshToken"));
            // Don't retry refresh endpoint itself
            if (originalRequest.url?.includes('RefreshToken')) {
                console.log('[Auth] Refresh endpoint itself returned 401 — forcing logout');
                forceLogout();
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // Queue this request until refresh completes
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                }).catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const userId = localStorage.getItem("UserId");
            const refreshToken = localStorage.getItem("RefreshToken");

            if (!userId || !refreshToken) {
                console.log('[Auth] No userId or refreshToken in localStorage — forcing logout');
                isRefreshing = false;
                forceLogout();
                return Promise.reject(error);
            }

            try {
                const res = await axios.post(
                    `${process.env.REACT_APP_API_BASE_URL}/User/RefreshToken`,
                    { userId: parseInt(userId), refreshToken }
                );

                const newToken = res.data.token;
                const newRefreshToken = res.data.refreshToken;

                console.log('[Auth] Token refreshed successfully');
                localStorage.setItem("Token", newToken);
                localStorage.setItem("RefreshToken", newRefreshToken);
                // The refresh response already carries freshly resolved roles and rights,
                // and we were throwing them away - so a permission changed in the matrix
                // never reached a live session and the person had to log out and back in
                // before it took effect. Persist them, and the next refresh applies it.
                // (user 2026-09-27: "local cant open saleskit" - right granted, session stale)
                try {
                    if (Array.isArray(res.data.roles)) localStorage.setItem("Roles", JSON.stringify(res.data.roles));
                    if (Array.isArray(res.data.roleRights)) localStorage.setItem("RoleRights", JSON.stringify(res.data.roleRights));
                } catch { /* private mode */ }

                isRefreshing = false;
                processQueue(null, newToken);

                // Retry the original request with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                console.log('[Auth] Token refresh FAILED:', refreshError.response?.status, refreshError.message);
                isRefreshing = false;
                processQueue(refreshError, null);
                forceLogout();
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
